import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  Input,
  InputNumber,
  message,
  Select,
  Table,
  DatePicker,
  Switch,
  Modal,
  Tag,
  Radio,
} from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import PageHeader from "../Layout/PageHeader";
import MissionFilters from "./MissionFilters";
import CourierSelect from "../shared/CourierSelect";
import { formatDate, formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { USER_STATUS, MISSION_AUDIENCE_ALL } from "../../constants/user";
import {
  MISSION_TYPE_OPTIONS,
  isCourierMissionType,
} from "../../constants/mission";
import { usePermissions } from "../App/PermissionContext";

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

// "All Users" is mutually exclusive with specific statuses: picking ALL clears the rest,
// and picking a specific status clears ALL.
const resolveAudience = (values) => {
  if (!values || values.length <= 1) return values;
  const lastSelected = values[values.length - 1];
  return lastSelected === MISSION_AUDIENCE_ALL
    ? [MISSION_AUDIENCE_ALL]
    : values.filter((v) => v !== MISSION_AUDIENCE_ALL);
};

const renderAudience = (statuses) =>
  !statuses || statuses.length === 0 || statuses.includes(MISSION_AUDIENCE_ALL)
    ? "All Users"
    : statuses.join(", ");

const Mission = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
  });
  const [missions, setMissions] = useState([]);
  const [filters, setFilters] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [editingMission, setEditingMission] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchMissions = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));
    try {
      const params = {
        ...(filters.type && { type: filters.type }),
        ...(filters.isActive !== undefined && { isActive: filters.isActive }),
        ...(showArchived && { archived: true }),
        limit: pagination.limit,
        page: pagination.currentPage,
      };
      const res = await axios.get("/api/missions", { params });
      setMissions(res.data.data);
      setPagination((prev) => ({
        ...prev,
        totalSize: res.data.pagination?.totalSize,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, filters, showArchived]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const data = {
        name: values.name,
        description: values.description,
        type: values.type,
        pointsReward: values.pointsReward,
        targetValue: values.targetValue,
        isActive: values.isActive ?? true,
        eligibleStatuses: values.eligibleStatuses,
        // The API rejects a courier on the other types, and a courier picked before switching the
        // type away can still sit in the form, so send it only for courier types.
        ...(isCourierMissionType(values.type) && {
          serviceProvider: values.serviceProvider,
        }),
        ...(values.dateRange && {
          startDate: values.dateRange[0].startOf("day").toISOString(),
          endDate: values.dateRange[1].endOf("day").toISOString(),
        }),
      };
      await axios.post("/api/missions", data);
      message.success("Mission created successfully");
      form.resetFields();
      fetchMissions();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const updateHandler = async (missionId, values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      // A mission carrying only one of the two dates cannot be shown in a range picker, so an empty
      // picker there is not a deliberate clear. Leave those keys out rather than wiping the date.
      const hasPartialRange =
        Boolean(editingMission?.startDate) !== Boolean(editingMission?.endDate);
      const data = {
        ...values,
        // The update applies only the keys it receives, so an emptied picker has to send an
        // explicit null -- omitting the keys silently leaves the old dates in place.
        ...(values.dateRange
          ? {
              startDate: values.dateRange[0].startOf("day").toISOString(),
              endDate: values.dateRange[1].endOf("day").toISOString(),
            }
          : hasPartialRange
          ? {}
          : { startDate: null, endDate: null }),
      };
      delete data.dateRange;
      // A courier mission's courier cannot change and the other types take none, so never send it.
      delete data.serviceProvider;
      await axios.patch(`/api/missions/${missionId}`, data);
      message.success("Mission updated successfully");
      setEditingMission(null);
      fetchMissions();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const toggleStatusHandler = async (missionId, isActive) => {
    try {
      await axios.patch(`/api/missions/${missionId}/toggle-status`, { isActive });
      message.success("Mission status updated");
      fetchMissions();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const archiveHandler = (missionId) => {
    Modal.confirm({
      title: "Archive Mission",
      content: "Are you sure you want to archive this mission?",
      async onOk() {
        try {
          await axios.patch(`/api/missions/${missionId}/archive`);
          message.success("Mission archived successfully");
          fetchMissions();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const unarchiveHandler = (missionId) => {
    Modal.confirm({
      title: "Restore Mission",
      content: "Restore this mission to the active list?",
      async onOk() {
        try {
          await axios.patch(`/api/missions/${missionId}/unarchive`);
          message.success("Mission restored successfully");
          fetchMissions();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const handleArchivedToggle = (e) => {
    setShowArchived(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (record) => {
    setEditingMission(record);
    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      type: record.type,
      serviceProvider: record.serviceProvider,
      pointsReward: record.pointsReward,
      targetValue: record.targetValue,
      isActive: record.isActive,
      eligibleStatuses:
        record.eligibleStatuses && record.eligibleStatuses.length
          ? record.eligibleStatuses
          : [MISSION_AUDIENCE_ALL],
      // Seed the range so the modal shows the mission's real dates. Left unseeded, the picker
      // renders empty for a dated mission and saving without touching it reads as a clear.
      // Dates come back as a plain ISO string or as Parse's { iso } shape, same as formatDate.
      dateRange:
        record.startDate && record.endDate
          ? [
              moment(record.startDate?.iso || record.startDate),
              moment(record.endDate?.iso || record.endDate),
            ]
          : undefined,
    });
  };

  // The API locks a courier mission's type and courier, and refuses to turn any other mission into
  // a courier one, so the edit modal never offers a switch either way.
  const editingCourierMission = isCourierMissionType(editingMission?.type);
  const editTypeOptions = editingCourierMission
    ? MISSION_TYPE_OPTIONS
    : MISSION_TYPE_OPTIONS.filter(
        (option) => !isCourierMissionType(option.value),
      );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Audience",
      dataIndex: "eligibleStatuses",
      key: "eligibleStatuses",
      width: 180,
      render: (statuses) => renderAudience(statuses),
    },
    {
      title: "Points Reward",
      dataIndex: "pointsReward",
      key: "pointsReward",
      width: 120,
    },
    {
      title: "Target Value",
      dataIndex: "targetValue",
      key: "targetValue",
      width: 120,
      render: (text) => text ?? "-",
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (value, record) =>
        !showArchived &&
        permissions?.includes(PERMISSIONS.TOGGLE_MISSION_STATUS) ? (
          <Switch
            checked={value}
            onChange={(checked) =>
              toggleStatusHandler(record.objectId, checked)
            }
          />
        ) : value ? (
          "Yes"
        ) : (
          "No"
        ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      render: (text) => (text ? formatDate(text) : "-"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: 120,
      render: (text) => (text ? formatDate(text) : "-"),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => (text ? formatDateTime(text) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) =>
        showArchived ? (
          permissions?.includes(PERMISSIONS.DELETE_MISSION) && (
            <Button
              type="link"
              onClick={() => unarchiveHandler(record.objectId)}
            >
              Restore
            </Button>
          )
        ) : (
          <>
            {permissions?.includes(PERMISSIONS.UPDATE_MISSION) && (
              <Button type="link" onClick={() => handleEdit(record)}>
                Edit
              </Button>
            )}
            {permissions?.includes(PERMISSIONS.DELETE_MISSION) && (
              <Button
                type="link"
                danger
                onClick={() => archiveHandler(record.objectId)}
              >
                Archive
              </Button>
            )}
          </>
        ),
    },
  ];

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  return (
    <div>
      <PageHeader
        title="Mission Management"
        childRoutes={[{ path: "/mission", breadcrumbName: "Mission" }]}
      />

      {permissions?.includes(PERMISSIONS.CREATE_MISSION) && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{ marginBottom: 16 }}
        >
          <Collapse.Panel header="Create New Mission" key="create">
            <Form
              form={form}
              layout="vertical"
              onFinish={createHandler}
              initialValues={{
                isActive: true,
                eligibleStatuses: [MISSION_AUDIENCE_ALL],
              }}
            >
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.type !== curr.type}
              >
                {({ getFieldValue }) => {
                  const courierType = isCourierMissionType(
                    getFieldValue("type"),
                  );

                  return (
                    <>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="name"
                            label="Name"
                            rules={[
                              { required: true, message: "Name is required" },
                            ]}
                          >
                            <Input placeholder="Mission name" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="type"
                            label="Type"
                            rules={[
                              { required: true, message: "Type is required" },
                            ]}
                          >
                            <Select
                              placeholder="Select type"
                              options={MISSION_TYPE_OPTIONS}
                            />
                          </Form.Item>
                        </Col>
                        {courierType && (
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              name="serviceProvider"
                              label="Courier"
                              rules={[
                                {
                                  required: true,
                                  message: "Courier is required",
                                },
                              ]}
                            >
                              <CourierSelect
                                placeholder="Select one courier"
                                showSearch
                              />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="pointsReward"
                            label="Points Reward"
                            rules={[
                              {
                                required: true,
                                type: "number",
                                min: 1,
                                message:
                                  "Points reward must be a number greater than 0",
                              },
                            ]}
                          >
                            <InputNumber min={1} style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="targetValue"
                            label="Target Value"
                            rules={[
                              {
                                required: courierType,
                                message: "Target value is required",
                              },
                              {
                                type: "number",
                                min: 1,
                                message:
                                  "Target value must be a number greater than 0",
                              },
                            ]}
                          >
                            <InputNumber min={1} style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            name="dateRange"
                            label="Date Range"
                            rules={[
                              {
                                required: courierType,
                                message:
                                  "Date range is required for courier missions",
                              },
                            ]}
                          >
                            <RangePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} sm={24} md={16}>
                          <Form.Item name="description" label="Description">
                            <Input.TextArea
                              rows={3}
                              placeholder="Mission description"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  );
                }}
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={16}>
                  <Form.Item
                    name="eligibleStatuses"
                    label="Eligible User Statuses"
                    rules={[
                      { required: true, message: "Audience is required" },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select audience"
                      onChange={(values) =>
                        form.setFieldsValue({
                          eligibleStatuses: resolveAudience(values),
                        })
                      }
                    >
                      <Select.Option value={MISSION_AUDIENCE_ALL}>
                        All Users
                      </Select.Option>
                      {Object.values(USER_STATUS).map((status) => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="isActive"
                    label="Active"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading.create}
              >
                Create Mission
              </Button>
            </Form>
          </Collapse.Panel>
        </Collapse>
      )}

      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <MissionFilters
            filters={filters}
            onFiltersChange={(filters) => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row justify="start" style={{ marginTop: 16, marginBottom: 8 }}>
        <Col>
          <Radio.Group
            value={showArchived}
            onChange={handleArchivedToggle}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value={false}>Active</Radio.Button>
            <Radio.Button value={true}>Archived</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={missions}
        pagination={false}
        loading={loading.index}
        scroll={{ x: true }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>

      <Modal
        title="Edit Mission"
        visible={!!editingMission}
        onCancel={() => setEditingMission(null)}
        onOk={() => {
          editForm.validateFields().then((values) => {
            updateHandler(editingMission.objectId, values);
          });
        }}
        confirmLoading={loading.update}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Type is required" }]}
          >
            <Select
              options={editTypeOptions}
              disabled={editingCourierMission}
            />
          </Form.Item>
          {editingCourierMission && (
            <Form.Item name="serviceProvider" label="Courier">
              <CourierSelect disabled />
            </Form.Item>
          )}
          <Form.Item
            name="pointsReward"
            label="Points Reward"
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                message: "Points reward must be a number greater than 0",
              },
            ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="targetValue"
            label="Target Value"
            rules={[
              {
                required: editingCourierMission,
                message: "Target value is required",
              },
              {
                type: "number",
                min: 1,
                message: "Target value must be a number greater than 0",
              },
            ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="eligibleStatuses"
            label="Eligible User Statuses"
            rules={[{ required: true, message: "Audience is required" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select audience"
              onChange={(values) =>
                editForm.setFieldsValue({
                  eligibleStatuses: resolveAudience(values),
                })
              }
            >
              <Select.Option value={MISSION_AUDIENCE_ALL}>
                All Users
              </Select.Option>
              {Object.values(USER_STATUS).map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {/* Clearable on the other types (PD-2497); the API rejects clearing a courier mission's window. */}
          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[
              {
                required: editingCourierMission,
                message: "Date range is required for courier missions",
              },
            ]}
          >
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Mission;
